/** Pure endpoint projection of authenticated report revisions. Never run on the relay. */
import type { PrivateReportFrame } from '@viko/protocol'
import { z } from 'zod'
import type { BuildCard, DoctorCheck, Landing } from './types.js'

type MachineFrame = Extract<PrivateReportFrame, { type: 'machine.status' }>
export type ReportProjection =
  | { kind: 'machine'; machineId: string; status: MachineFrame; doctor: DoctorCheck[] }
  | { kind: 'landing'; machineId: string; landing: Landing }
  | {
      kind: 'build'
      machineId: string
      build: BuildCard
      report: Extract<PrivateReportFrame, { type: 'build.update' }>
    }
  | {
      kind: 'hygiene'
      machineId: string
      report: Extract<PrivateReportFrame, { type: 'hygiene.report' }>
    }
  | {
      kind: 'load'
      machineId: string
      report: Extract<PrivateReportFrame, { type: 'machine.load' }>
    }
  | {
      kind: 'projects'
      machineId: string
      report: Extract<PrivateReportFrame, { type: 'machine.projects' }>
    }
  | {
      kind: 'usage'
      machineId: string
      report: Extract<PrivateReportFrame, { type: 'run.usage' }>
    }
  | {
      kind: 'presence'
      machineId: string
      report: Extract<PrivateReportFrame, { type: 'machine.presence' }>
    }
const FixCard = z.object({
  checkId: z.string(),
  severity: z.enum(['blocker', 'warning']),
  title: z.string(),
  problem: z.string(),
  fix: z.object({
    text: z.string(),
    command: z.string().optional(),
    autoFix: z.boolean().optional(),
  }),
})
const Build = z.object({
  buildId: z.string().optional(),
  projectId: z.string().optional(),
  laneId: z.string().optional(),
  machineId: z.string().optional(),
  laneName: z.string().optional(),
  production: z.boolean().optional(),
  version: z
    .object({
      label: z.string().optional(),
      buildNumber: z.union([z.string(), z.number()]).optional(),
    })
    .optional(),
  detail: z.string().optional(),
  changelog: z
    .array(
      z.object({
        taskId: z.string().nullable(),
        title: z.string(),
        workstreamId: z.string().nullable(),
      }),
    )
    .optional(),
  taskIds: z.array(z.string()).optional(),
  links: z.array(z.object({ label: z.string().optional(), url: z.string() })).optional(),
  failure: z
    .object({
      cause: z.string().optional(),
      fix: z.string().optional(),
      category: z.string().optional(),
      evidence: z.string().optional(),
    })
    .optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
  // ---- release-lanes-v2 ----
  headCommit: z.string().optional(),
  live: z
    .object({
      environment: z.string(),
      version: z.string(),
      commit: z.string(),
      since: z.number(),
      verifiedAt: z.number(),
      source: z.string().optional(),
      detail: z.string().optional(),
      url: z.string().optional(),
    })
    .optional(),
  handoffs: z
    .array(
      z.object({
        kind: z.string(),
        label: z.string(),
        url: z.string(),
        note: z.string().optional(),
      }),
    )
    .optional(),
  // ---- end release-lanes-v2 ----
})
export function projectReport(
  machineId: string,
  frame: PrivateReportFrame,
  previous?: ReportProjection,
): ReportProjection {
  switch (frame.type) {
    case 'machine.status': {
      const status = { ...(previous?.kind === 'machine' ? previous.status : {}), ...frame }
      const doctor = (status.doctor?.fixCards ?? []).map((raw) => {
        const c = FixCard.parse(raw)
        return {
          id: c.checkId,
          checkId: c.checkId,
          title: c.title,
          status: c.severity === 'blocker' ? ('fail' as const) : ('warn' as const),
          detail: c.problem,
          fix: c.fix.text,
          command: c.fix.command,
          autoFix: c.fix.autoFix,
        }
      })
      return { kind: 'machine', machineId, status, doctor }
    }
    case 'landing.update': {
      const before = previous?.kind === 'landing' ? previous.landing : undefined
      const result =
        frame.result && typeof frame.result === 'object'
          ? (frame.result as Record<string, unknown>)
          : {}
      const text = (...keys: string[]) =>
        keys.map((k) => result[k]).find((v): v is string => typeof v === 'string')
      const steps = [...(before?.steps ?? [])]
      if (frame.step)
        steps.push({
          step: frame.step,
          status: frame.stepStatus,
          detail: frame.detail,
          ts: frame.ts,
          ...('attempt' in frame ? { attempt: frame.attempt } : {}),
        })
      return {
        kind: 'landing',
        machineId,
        landing: {
          id: frame.landingId,
          machineId,
          projectId: frame.projectId ?? before?.projectId ?? null,
          workstreamId: frame.workstreamId ?? before?.workstreamId ?? null,
          runId: frame.runId ?? before?.runId ?? null,
          branch: frame.branch ?? before?.branch ?? null,
          summary: frame.summary ?? before?.summary ?? null,
          taskIds: frame.taskIds ?? before?.taskIds ?? [],
          state: frame.state,
          step: frame.step ?? before?.step ?? null,
          steps: steps.slice(-100),
          commit: text('sha', 'commit', 'mergedSha', 'headSha') ?? before?.commit ?? null,
          error:
            frame.state === 'failed'
              ? (frame.reason ??
                frame.failure?.message ??
                text('error', 'reason', 'message') ??
                frame.detail ??
                'landing failed')
              : null,
          createdAt: before?.createdAt ?? frame.ts,
          updatedAt: frame.ts,
          ...((frame.auto ?? before?.auto) ? { auto: true } : {}),
          ...((frame.headSha ?? before?.headSha)
            ? { headSha: frame.headSha ?? before?.headSha }
            : {}),
          ...((frame.recipe ?? before?.recipe) ? { recipe: frame.recipe ?? before?.recipe } : {}),
          ...((frame.state === 'failed' || frame.state === 'interrupted') && frame.reason
            ? { reason: frame.reason }
            : {}),
          ...((frame.failure ?? before?.failure) && frame.state !== 'landed'
            ? { failure: frame.failure ?? before?.failure }
            : {}),
          // ---- landing-modes ----
          ...((frame.targetBranch ?? before?.targetBranch)
            ? { targetBranch: frame.targetBranch ?? before?.targetBranch }
            : {}),
          ...((frame.gate ?? before?.gate) ? { gate: frame.gate ?? before?.gate } : {}),
          ...(frame.waitingOn && frame.state !== 'landed' && frame.state !== 'cancelled'
            ? { waitingOn: frame.waitingOn }
            : {}),
          // ---- end landing-modes ----
          ...(frame.state === 'failed' && frame.baselineFailing !== undefined
            ? { baselineFailing: frame.baselineFailing }
            : {}),
        },
      }
    }
    case 'build.update': {
      const r = Build.parse(frame.record)
      if (
        (r.machineId && r.machineId !== machineId) ||
        (r.buildId && r.buildId !== frame.buildId) ||
        (r.projectId && r.projectId !== frame.projectId) ||
        (r.laneId && r.laneId !== frame.laneId)
      )
        throw new Error('wrong build report')
      const tasks = r.changelog ?? [],
        links = r.links ?? []
      return {
        kind: 'build',
        machineId,
        report: frame,
        build: {
          buildId: frame.buildId,
          projectId: frame.projectId,
          laneId: frame.laneId,
          title: `Build ${r.version?.buildNumber ?? r.version?.label ?? frame.buildId} on ${r.laneName ?? frame.laneId}`,
          subtitle:
            tasks
              .slice(0, 5)
              .map((t) => t.title)
              .join(' · ') + (tasks.length > 5 ? ` +${tasks.length - 5}` : ''),
          status: frame.status,
          detail: r.detail ?? null,
          version: r.version?.label ?? null,
          production: r.production ?? false,
          tasks,
          taskIds: r.taskIds ?? [],
          links,
          failure: r.failure ?? null,
          actions: [
            ...(frame.status === 'available' && links.length ? ['open'] : []),
            'feedback',
            'view-log',
            ...(frame.status === 'failed' && r.failure?.category !== 'code' ? ['retry'] : []),
            ...(frame.status === 'available' && r.handoffs?.some((h) => h.kind === 'install-mac')
              ? ['install-mac']
              : []),
          ],
          createdAt: r.createdAt ?? null,
          updatedAt: r.updatedAt ?? null,
          ...(r.headCommit ? { headCommit: r.headCommit } : {}),
          ...(r.live ? { live: r.live } : {}),
          ...(r.handoffs?.length ? { handoffs: r.handoffs } : {}),
        },
      }
    }
    case 'hygiene.report':
      return { kind: 'hygiene', machineId, report: frame }
    // Each revision is the machine's full list: the latest replaces the previous one.
    case 'machine.projects':
      return { kind: 'projects', machineId, report: frame }
    // g4-cost: each revision is the machine's recent-run usage list; the latest replaces it.
    case 'run.usage':
      return { kind: 'usage', machineId, report: frame }
    // Latest screen-lock / system-idle state of this machine (app lock signal).
    case 'machine.presence':
      return { kind: 'presence', machineId, report: frame }
    // G1: each revision is the machine's full load snapshot and week of history.
    case 'machine.load':
      return { kind: 'load', machineId, report: frame }
  }
}
